import { clearImgData, sendImgDataFromStorage } from "common-lib";
import { useCallback, useEffect, useRef, useState } from "react";
import { getImgData } from "../services/fileService";

export type AiImageRes = any;

export interface FetchAiImageParams {
  path: string;
  imageViewMode: string;
  isMulti: boolean;
  viewMode: string;
  specId?: string;
}

export interface InspImageState {
  data: AiImageRes | null;
  originImage: string;
  scoremap: string[] | null;
  processImage: string | null;
  loading: boolean;
  error: Error | null;
}

const EMPTY: InspImageState = {
  data: null,
  originImage: "",
  scoremap: null,
  processImage: null,
  loading: false,
  error: null,
};

export function useInspImage(serverAddress: string | undefined, trainUuid: string | undefined) {
  const [state, setState] = useState<InspImageState>(EMPTY);

  const reqIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reset = useCallback(() => {
    reqIdRef.current += 1;
    setState(EMPTY);
  }, []);

  const fetchAiImgData = useCallback(
    async ({ path, imageViewMode, isMulti, viewMode, specId }: FetchAiImageParams) => {
      const uuid = specId ?? trainUuid;
      if (!serverAddress || !uuid) {
        setState({ ...EMPTY, error: new Error("서버 주소 또는 모델 정보가 없습니다.") });
        return;
      }

      const reqId = ++reqIdRef.current;
      const isStale = () => !mountedRef.current || reqIdRef.current !== reqId;

      setState({ ...EMPTY, loading: true });

      const sendData = {
        uuid,
        path,
        destination: {
          index1: "tag",
          index2: "image_bytes_original",
          index3: imageViewMode === "scoremap" ? "scoremap" : "image_bytes_process",
          ...(isMulti && {
            index4: "score_list",
            index5: "label_total",
            index6: "threshold_list",
          }),
        },
      };

      try {
        await sendImgDataFromStorage(serverAddress, sendData);
        if (isStale()) return;

        const res: AiImageRes = await getImgData(serverAddress, uuid, isMulti, viewMode);
        if (isStale()) return;

        if (res?.code) {
          setState({ ...EMPTY, error: new Error(res.message ?? "이미지를 가져오지 못했습니다.") });
          return;
        }

        setState({
          data: res,
          originImage: typeof res?.originImage === "string" ? res.originImage : "",
          processImage: typeof res?.processImage === "string" ? res.processImage : null,
          scoremap: Array.isArray(res?.scoremap) ? res.scoremap : null,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (isStale()) return;
        setState({ ...EMPTY, error: err instanceof Error ? err : new Error(String(err)) });
      } finally {
        try {
          await clearImgData(serverAddress, uuid);
        } catch (clearErr) {
          console.error("clearImgData 실패", clearErr);
        }
      }
    },
    [serverAddress, trainUuid],
  );

  return { ...state, fetchAiImgData, reset };
}

/**
 * isMulti 응답에서 라벨 패널이 쓸 세 배열을 꺼냅니다.
 * 셋 중 하나라도 배열이 아니면 빈 배열을 돌려줘서 패널이 빈 상태를 그리게 합니다.
 */
export function pickLabelScores(data: AiImageRes | null) {
  const labels = Array.isArray(data?.label_total) ? (data.label_total as string[]) : [];
  const scoreList = Array.isArray(data?.score_list) ? (data.score_list as number[]) : [];
  const thresholdList = Array.isArray(data?.threshold_list) ? (data.threshold_list as number[]) : [];

  if (labels.length === 0 || scoreList.length === 0 || thresholdList.length === 0) {
    return { labels: [], scoreList: [], thresholdList: [] };
  }
  return { labels, scoreList, thresholdList };
}
